import React, { useState, useCallback, useRef, useEffect } from 'react';
import FeedbackThread from 'features/feedback/FeedbackThread';
import {
  Fab,
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CloseIcon from '@mui/icons-material/Close';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import DeleteIcon from '@mui/icons-material/Delete';
import { makeStyles } from 'tss-react/mui';
import {
  submitFeedback,
  getMyFeedbackTickets,
  replyToFeedbackTicket,
  type FeedbackTicket,
} from 'API/feedback';
import { OPEN_FEEDBACK_EVENT, type SupportPrefill } from 'utils/support.utils';

const useStyles = makeStyles()((theme) => ({
  fab: {
    position: 'fixed',
    top: '50%',
    right: theme.spacing(2),
    transform: 'translateY(-50%)',
    zIndex: theme.zIndex.speedDial,
  },
  drawerPaper: {
    width: 420,
    maxWidth: '100vw',
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  screenshotPreview: {
    width: '100%',
    maxHeight: 180,
    objectFit: 'contain',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.action.hover,
  },
  screenshotPlaceholder: {
    width: '100%',
    height: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
    border: `2px dashed ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    flexDirection: 'column',
    gap: theme.spacing(1),
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  captureRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const FeedbackWidget: React.FC = () => {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  /**
   * The reporter's own tickets, with their conversations.
   *
   * Feedback was one-way until now: you filed a report, received an email, and had no way
   * to answer a follow-up question except by filing another ticket. This tab is that way
   * back — and it is where the "reply" link in our emails lands.
   */
  const [tab, setTab] = useState<'new' | 'mine'>('new');
  const [myTickets, setMyTickets] = useState<FeedbackTicket[]>([]);
  const [loadingMine, setLoadingMine] = useState(false);
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null);

  const loadMyTickets = useCallback(async () => {
    setLoadingMine(true);
    try {
      const res = await getMyFeedbackTickets();
      setMyTickets(res.items || []);
    } catch {
      setErrorMsg('Deine Tickets konnten nicht geladen werden.');
    } finally {
      setLoadingMine(false);
    }
  }, []);

  // Errors surface inside the thread, which keeps the draft — so this deliberately rethrows
  // rather than swallowing the failure into a banner.
  const sendReply = useCallback(async (ticketId: string, body: string) => {
    const updated = await replyToFeedbackTicket(ticketId, body);
    setMyTickets((prev) => prev.map((t) => (t._id === ticketId ? updated : t)));
  }, []);
  const drawerRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => setOpen(true);

  /**
   * "Melde dich bei uns" from anywhere in the app — see `utils/support.utils`.
   *
   * A prefilled subject is the point of this, not a convenience: the sync-error screens know
   * exactly which error the user is looking at, and a ticket that says which one beats the
   * ones we get today. An open draft is never overwritten — losing typed text to a stray
   * event would be a worse bug than the one this fixes.
   */
  useEffect(() => {
    const onOpenRequest = (event: Event) => {
      const { title: prefillTitle, description: prefillDescription } =
        (event as CustomEvent<SupportPrefill>).detail || {};
      setTab('new');
      setOpen(true);
      setTitle((current) => (current ? current : prefillTitle || ''));
      setDescription((current) => (current ? current : prefillDescription || ''));
    };
    window.addEventListener(OPEN_FEEDBACK_EVENT, onOpenRequest);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, onOpenRequest);
  }, []);

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setScreenshotDataUrl(null);
    setErrorMsg(null);
  };

  const handleCaptureScreenshot = useCallback(async () => {
    setCapturing(true);
    // Temporarily hide the drawer so it doesn't appear in the screenshot
    if (drawerRef.current) drawerRef.current.style.visibility = 'hidden';

    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: 0.75,
        logging: false,
      });
      const dataUrl = canvas.toDataURL('image/png');
      setScreenshotDataUrl(dataUrl);
    } catch {
      setErrorMsg('Screenshot capture failed. You can still submit without one.');
    } finally {
      if (drawerRef.current) drawerRef.current.style.visibility = '';
      setCapturing(false);
    }
  }, []);

  const handleRemoveScreenshot = () => setScreenshotDataUrl(null);

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      setErrorMsg('Please fill in title and description.');
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);

    try {
      await submitFeedback({
        title: title.trim(),
        description: description.trim(),
        pageUrl: window.location.href,
        screenshotBase64: screenshotDataUrl ?? undefined,
      });
      setSuccessOpen(true);
      setOpen(false);
      resetForm();
    } catch {
      setErrorMsg('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title="Give Feedback" placement="left">
        <Fab
          color="primary"
          aria-label="feedback"
          className={classes.fab}
          onClick={handleOpen}
          size="medium"
        >
          <FeedbackIcon />
        </Fab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ className: classes.drawerPaper, ref: drawerRef }}
      >
        <Box className={classes.header}>
          <Typography variant="h6" fontWeight={600}>
            Give Feedback
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            if (v === 'mine') loadMyTickets();
          }}
          variant="fullWidth"
          sx={{ mb: 1 }}
        >
          <Tab value="new" label="Neues Feedback" />
          <Tab value="mine" label="Meine Tickets" />
        </Tabs>

        {tab === 'mine' ? (
          <Stack gap={1.5}>
            {loadingMine && <Typography variant="body2" color="text.secondary">Wird geladen…</Typography>}
            {!loadingMine && myTickets.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Du hast noch kein Feedback gesendet.
              </Typography>
            )}
            {myTickets.map((ticket) => {
              const isOpen = expandedTicketId === ticket._id;
              return (
                <Box key={ticket._id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                    onClick={() => setExpandedTicketId(isOpen ? null : ticket._id)}
                  >
                    <Chip
                      size="small"
                      label={ticket.status === 'done' ? 'Erledigt' : ticket.status === 'in_progress' ? 'In Arbeit' : 'Offen'}
                      color={ticket.status === 'done' ? 'success' : ticket.status === 'in_progress' ? 'info' : 'default'}
                    />
                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{ticket.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>

                  {isOpen && (
                    <Box sx={{ mt: 1.5 }}>
                      <FeedbackThread
                        messages={ticket.messages}
                        description={ticket.description}
                        viewerRole="reporter"
                        onReply={(body) => sendReply(ticket._id, body)}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        ) : (
        <>
        <Typography variant="body2" color="text.secondary">
          Help us improve by sharing what you think. You can attach a screenshot of the current page.
        </Typography>

        <TextField
          label="Title"
          placeholder="Short summary of your feedback"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          size="small"
          required
          inputProps={{ maxLength: 200 }}
        />

        <TextField
          label="Description"
          placeholder="Describe your feedback in detail..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          maxRows={8}
          required
          inputProps={{ maxLength: 5000 }}
        />

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Screenshot (optional)
          </Typography>

          {screenshotDataUrl ? (
            <Box>
              <img
                src={screenshotDataUrl}
                alt="Screenshot preview"
                className={classes.screenshotPreview}
              />
              <Box className={classes.captureRow} sx={{ mt: 0.5 }}>
                <Button
                  size="small"
                  startIcon={<ScreenshotMonitorIcon />}
                  onClick={handleCaptureScreenshot}
                  disabled={capturing}
                >
                  Recapture
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleRemoveScreenshot}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              className={classes.screenshotPlaceholder}
              onClick={capturing ? undefined : handleCaptureScreenshot}
            >
              {capturing ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <ScreenshotMonitorIcon />
                  <Typography variant="caption">Click to capture screenshot</Typography>
                </>
              )}
            </Box>
          )}
        </Box>

        {errorMsg && (
          <Alert severity="error" onClose={() => setErrorMsg(null)}>
            {errorMsg}
          </Alert>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 'auto' }}>
          <Button variant="outlined" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !title.trim() || !description.trim()}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </Stack>
        </>
        )}
      </Drawer>

      <Snackbar
        open={successOpen}
        autoHideDuration={5000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessOpen(false)}>
          Thank you! Your feedback has been submitted.
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackWidget;
